import Info from "@assets/settings/info.svg";
import React from "react";
import { TransactionByIdResponseData } from "../../../services/models/Transactions/TransactionByIdModel";
import ConstantFunctions from "../../../utils/constants_functions";
import LabelRow from "./LabelRow";
import TransactionTypeSymbols from "./TransactionTypeSymbols";
import { getTransactionTypeText } from "./TransactionTypeText";

interface MerchantPrimeTransactionContainerProps {
  transactionData: TransactionByIdResponseData;
}

const PrimeTransactionContainer: React.FC<
  MerchantPrimeTransactionContainerProps
> = ({ transactionData }) => {
  const constantFunctions = new ConstantFunctions();

  const displayValueOrDash = (value: any) => (value ? value.toString() : "--");
  const isValuePresent = (value: any) => !!value;

  const isCashierTransaction = transactionData.isCashier === 1 ? true : false;
  const isINR =
    (transactionData &&
      transactionData?.countryCode &&
      transactionData?.countryCode === "IN") ||
    transactionData?.fiat === "INR"
      ? true
      : false;

  var payinPayoutType = "";
  if (transactionData?.ledger === "CR") {
    payinPayoutType = "Credited";
  } else if (transactionData?.ledger === "DR") {
    payinPayoutType = "Debited";
  } else {
    payinPayoutType = transactionData?.tradeType;
  }

  return (
    <div className="border border-bg-header overflow-scroll mt-[20px] flex flex-col rounded-lg mx-[20px]">
      <div className="border-b border-b-bg-header px-[16px] py-[18px] flex flex-row justify-between">
        <div className="flex flex-row">
          <TransactionTypeSymbols
            transactionType={transactionData?.transactionType ?? ""}
            ledger={transactionData?.ledger ?? ""}
          />
          <span className="text-[16px] font-medium text-text-base">
            {transactionData?.ledger === "CR" ? "Received" : "Sent"}
          </span>
        </div>
        <div className="flex flex-row items-center gap-[4px]">
          <span className="text-[16px] font-bold text-text-base">
            {displayValueOrDash(
              constantFunctions.formatAmount(transactionData?.amount),
            )}
          </span>
          <img
            src={transactionData?.cryptoCurrencyImage}
            width={18}
            height={18}
          />
        </div>
      </div>
      <div className="flex flex-col space-y-[16px] p-[16px]">
        <LabelRow
          label={"Transaction Type"}
          value={displayValueOrDash(
            getTransactionTypeText(
              transactionData?.transactionType ?? "",
              transactionData?.internalType ?? "",
            ),
          )}
        />
        <LabelRow
          label={"Date & Time"}
          value={`${constantFunctions.formatDateTime(
            transactionData?.updatedAt ?? transactionData?.createdAt ?? "",
          )}`}
        />
      </div>
      <div className="border-b border-b-bg-header"></div>
      <div className="flex flex-col space-y-[16px] p-[16px]">
        <div className="flex flex-row items-center gap-[4px]">
          <div className="bg-bg-header rounded-full h-[24px] p-[2px] w-[24px] mr-[8px] flex items-center justify-center">
            <img src={Info} alt="Info" />
          </div>
          <span className="text-[14px] font-normal text-text-base">
            Cross Ramp Transaction Details
          </span>
        </div>
        <LabelRow
          label={"Trade ID"}
          value={displayValueOrDash(transactionData?.tradeId)}
          isCopy={isValuePresent(transactionData?.tradeId)}
        />
        <LabelRow
          label={"MOID"}
          value={displayValueOrDash(transactionData?.merchantOrderId)}
          isCopy={isValuePresent(transactionData?.merchantOrderId)}
        />
        {isINR && (
          <>
            <LabelRow
              label={"Cx UTR No."}
              value={displayValueOrDash(transactionData?.utr)}
              isCopy={isValuePresent(transactionData?.utr)}
            />
            <LabelRow
              label={"Cashier UTR No."}
              value={displayValueOrDash(transactionData?.cashierUTR)}
              isCopy={isValuePresent(transactionData?.cashierUTR)}
            />
          </>
        )}
        <LabelRow
          label={"Trade Type"}
          value={displayValueOrDash(transactionData?.tradeType)}
        />
        {!isCashierTransaction && (
          <LabelRow
            label={"Customer email Id"}
            value={displayValueOrDash(transactionData?.customerEmailId)}
            isCopy={isValuePresent(transactionData?.customerEmailId)}
          />
        )}
        <LabelRow
          label={"Payment Method"}
          value={displayValueOrDash(transactionData?.paymentMethod)}
        />
        {isINR && (
          <LabelRow
            label={"UPI ID"}
            value={displayValueOrDash(transactionData?.upiId)}
            isCopy={isValuePresent(transactionData?.upiId)}
          />
        )}
        <LabelRow
          label={"Token | Fiat"}
          value={`${displayValueOrDash(
            transactionData.token,
          )} | ${displayValueOrDash(transactionData.fiat)}`}
        />
        <LabelRow
          label={"Amount In fiat"}
          value={`${displayValueOrDash(
            constantFunctions.formatAmount(transactionData.fiatAmount),
          )} ${displayValueOrDash(transactionData.fiat)}`}
        />
        <LabelRow
          label={"Amount In Crypto"}
          value={`${displayValueOrDash(
            constantFunctions.formatAmount(transactionData.tradeAmount),
          )} ${displayValueOrDash(transactionData.token)}`}
        />
        <LabelRow
          label={"Conversion Rate"}
          valueComp={
            <div className="flex flex-row items-center">
              <p className="text-text-base text-[12px] font-medium mr-[4px]">
                {displayValueOrDash(
                  constantFunctions.formatAmount(
                    transactionData.conversionRate,
                  ),
                )}
              </p>
              {transactionData.fiatCurrencyImage && (
                <img
                  src={transactionData.fiatCurrencyImage}
                  height={16}
                  width={16}
                  className="mr-[4px]"
                />
              )}
              <p className="text-text-base text-[12px] font-medium mr-[4px]">
                =
              </p>
              <p className="text-text-base text-[12px] font-medium mr-[4px]">
                1.00
              </p>
              {transactionData.cryptoCurrencyImage && (
                <img
                  src={transactionData.cryptoCurrencyImage}
                  height={16}
                  width={16}
                />
              )}
            </div>
          }
        />
        {!isCashierTransaction && (
          <LabelRow
            label={"MDR"}
            value={`${displayValueOrDash(transactionData.MDR)}%`}
          />
        )}
        {!isINR && (
          <LabelRow
            label={"Fees"}
            value={`${displayValueOrDash(
              constantFunctions.formatAmount(transactionData.tyltFees),
            )} ${displayValueOrDash(transactionData.token)}`}
          />
        )}
        <LabelRow
          label={`${payinPayoutType} In Crypto`}
          value={`${displayValueOrDash(
            constantFunctions.formatAmount(transactionData?.amount),
          )} ${displayValueOrDash(transactionData.token)}`}
        />
      </div>
    </div>
  );
};

export default PrimeTransactionContainer;
